import React, { useState } from 'react';
import { Box, ButtonBase, Button, Typography } from '@mui/material';
import { Check, MessageCircle, Phone, RefreshCw } from 'lucide-react';
import { formatPhone } from '../../common/formatters';

/**
 * Peças visuais compartilhadas das telas públicas do cliente final
 * (portal do orçamento). Espelham, valor a valor, o visual de
 * `CustomerOrderDisplayScreen.js` (tela pública da OS, referência de design):
 * mesmos breakpoints, cards, cabeçalho da empresa, hero em gradiente, stepper,
 * bloco de totais, skeleton e telas de feedback.
 *
 * A tela da OS mantém suas cópias locais (não foi alterada de propósito para
 * não arriscar regressão); ao mexer no visual de uma, replicar na outra.
 */

// Breakpoints do protótipo (container queries -> media queries de viewport).
export const MQ_520 = '@media (min-width:520px)';
export const MQ_860 = '@media (min-width:860px)';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

export function normalizeStatus(status) {
    return String(status ?? '')
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, '')
        .trim()
        .toLowerCase();
}

/** Valores da API vêm em pt-BR sem "R$" (ex. "1.086,00"). */
export function parseMoney(value) {
    if (typeof value === 'number') return Number.isFinite(value) ? value : 0;
    if (value === undefined || value === null || value === '') return 0;
    const str = String(value).trim();
    const normalized = str.includes(',') ? str.replace(/\./g, '').replace(',', '.') : str;
    const parsed = parseFloat(normalized);
    return Number.isNaN(parsed) ? 0 : parsed;
}

const brlFormatter = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' });
export const formatCurrency = (value) => brlFormatter.format(parseMoney(value));

const WEEKDAYS = ['dom', 'seg', 'ter', 'qua', 'qui', 'sex', 'sáb'];

/** "18/09/2026 09:12:40" -> { date, dow, time } | null (sem data / "01/01/0001"). */
export function parseDateTime(value) {
    if (!value || /\/0001/.test(value)) return null;
    const match = String(value).match(/^(\d{2})\/(\d{2})\/(\d{4})(?:\s+(\d{2}):(\d{2}))?/);
    if (!match) return null;
    const date = new Date(Number(match[3]), Number(match[2]) - 1, Number(match[1]));
    return {
        date: `${match[1]}/${match[2]}/${match[3]}`,
        dow: WEEKDAYS[date.getDay()],
        time: match[4] ? `${match[4]}:${match[5]}` : '',
    };
}

/** Telefone BR -> dígitos com DDI 55 (sem duplicar se já vier com 55). */
export function toInternationalPhone(value) {
    const d = String(value ?? '').replace(/\D/g, '');
    if (!d) return '';
    return d.length >= 12 && d.startsWith('55') ? d : `55${d}`;
}

// ---------------------------------------------------------------------------
// Estrutura da página
// ---------------------------------------------------------------------------

/** Container <main> padrão (largura, grid, paddings responsivos). */
export function PortalMain({ children, ...rest }) {
    return (
        <Box
            component="main"
            sx={{
                width: '100%', maxWidth: 1040, mx: 'auto', display: 'grid', gap: '14px',
                p: '16px 16px 24px',
                [MQ_520]: { p: '20px 24px 28px' },
                [MQ_860]: { gap: '18px', p: '24px 24px 32px' },
            }}
            {...rest}
        >
            {children}
        </Box>
    );
}

export function BrandBar({ name, document: doc, logo, phone }) {
    const [logoFailed, setLogoFailed] = useState(false);
    const initial = name ? name.charAt(0).toUpperCase() : 'E';
    const showLogo = !!logo && /^(https?:\/\/|data:image\/)/i.test(String(logo).trim()) && !logoFailed;

    return (
        <Box
            component="header"
            sx={(t) => ({ bgcolor: 'background.paper', borderBottom: `1px solid ${t.palette.divider}` })}
        >
            <Box
                sx={{
                    maxWidth: 1040, mx: 'auto', display: 'flex', alignItems: 'center', gap: '12px', p: '14px 16px',
                    [MQ_520]: { p: '16px 24px' },
                }}
            >
                <Box
                    sx={(t) => ({
                        width: 48, height: 48, borderRadius: '14px', flexShrink: 0, overflow: 'hidden',
                        backgroundImage: t.palette.gradient.brand, color: '#fff', display: 'flex',
                        alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: 19,
                        boxShadow: '0 4px 14px rgba(37,99,235,.28)',
                    })}
                >
                    {showLogo ? (
                        <Box
                            component="img"
                            src={logo}
                            alt={`Logo da ${name || 'empresa'}`}
                            onError={() => setLogoFailed(true)}
                            sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        />
                    ) : initial}
                </Box>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography sx={{ fontSize: 15, fontWeight: 800, letterSpacing: '-.01em', lineHeight: 1.25 }}>
                        {name || 'Empresa'}
                    </Typography>
                    {doc && <Typography sx={{ fontSize: 11.5, color: 'text.disabled' }}>{doc}</Typography>}
                </Box>
                {phone && (
                    <Box sx={{ display: 'none', alignItems: 'center', gap: '8px', fontSize: 12.5, color: 'text.secondary', fontWeight: 600, [MQ_860]: { display: 'flex' } }}>
                        <Phone size={15} />
                        {formatPhone(phone)}
                    </Box>
                )}
            </Box>
        </Box>
    );
}

export function PoweredBy({ text }) {
    return (
        <Box
            sx={{
                maxWidth: 1040, mx: 'auto', width: '100%', p: '4px 16px 28px', display: 'flex',
                alignItems: 'center', justifyContent: 'center', gap: '8px', fontSize: 11,
                color: 'text.disabled', fontWeight: 600,
            }}
        >
            <Box
                sx={(t) => ({
                    width: 18, height: 18, borderRadius: '6px', backgroundImage: t.palette.gradient.brand,
                    color: '#fff', fontSize: 8.5, fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center',
                })}
            >
                FV
            </Box>
            {text}
        </Box>
    );
}

// ---------------------------------------------------------------------------
// Hero de status
// ---------------------------------------------------------------------------

/**
 * @param eyebrow    texto pequeno acima do título ("Orçamento")
 * @param number     número do documento
 * @param meta       { label, title, msg, Icon, step }
 * @param tiles      [{ icon, label, content }]  (content: JSX com <b>/<small>)
 * @param stepLabels etapas do stepper (usado se meta.step >= 0)
 * @param stepDone   marca a última etapa como concluída (estado final)
 */
export function Hero({ eyebrow, number, meta, tiles = [], stepLabels = [], stepDone, footLeft, onRefresh, refreshing, refreshLabel }) {
    const StatusIcon = meta.Icon;

    return (
        <Box
            component="section"
            sx={(t) => ({
                '--h-fg': '#fff', '--h-fg2': 'rgba(255,255,255,.82)', '--h-tile': 'rgba(255,255,255,.14)',
                '--h-tile-b': 'rgba(255,255,255,.24)', '--h-ink': t.palette.primary.main,
                position: 'relative', overflow: 'hidden', color: 'var(--h-fg)',
                borderRadius: '20px', p: '18px 18px 16px', boxShadow: t.customShadows.md,
                backgroundImage: t.palette.gradient.brand,
                '&::after': {
                    content: '""', position: 'absolute', right: -60, top: -60, width: 200, height: 200,
                    borderRadius: '50%', bgcolor: 'rgba(255,255,255,.08)', pointerEvents: 'none',
                },
                [MQ_520]: { p: '22px 24px 18px' },
                '@media print': {
                    '--h-fg': '#000', '--h-fg2': '#333', '--h-tile': '#f3f4f6', '--h-tile-b': '#ccc', '--h-ink': '#fff',
                    backgroundImage: 'none', backgroundColor: '#fff', border: '1px solid #ccc', boxShadow: 'none',
                    '&::after': { display: 'none' },
                },
            })}
        >
            <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px', flexWrap: 'wrap' }}>
                <Typography sx={{ fontSize: 10.5, fontWeight: 800, letterSpacing: '.07em', textTransform: 'uppercase', color: 'var(--h-fg2)' }}>
                    {eyebrow}{' '}
                    <Box component="b" sx={{ color: 'var(--h-fg)', fontSize: 12.5, letterSpacing: '.02em' }}>Nº {number}</Box>
                </Typography>
                <Box
                    sx={(t) => ({
                        display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: 11.5, fontWeight: 700,
                        px: '11px', py: '5px', borderRadius: '20px', whiteSpace: 'nowrap',
                        bgcolor: 'rgba(255,255,255,.22)', color: '#fff',
                        '@media print': { bgcolor: t.palette.grey[200], color: '#000' },
                    })}
                >
                    <StatusIcon size={13} strokeWidth={2.4} />
                    {meta.label}
                </Box>
            </Box>

            <Box
                sx={{
                    position: 'relative', zIndex: 1, display: 'grid', gap: '14px', mt: '12px',
                    [MQ_860]: { gridTemplateColumns: '1fr auto', alignItems: 'end', gap: '24px' },
                }}
            >
                <Box>
                    <Typography
                        component="h1"
                        sx={{ m: 0, fontSize: 22, fontWeight: 800, letterSpacing: '-.02em', lineHeight: 1.2, [MQ_520]: { fontSize: 26 } }}
                    >
                        {meta.title}
                    </Typography>
                    <Typography sx={{ mt: '6px', fontSize: 13, lineHeight: 1.5, color: 'var(--h-fg2)' }}>{meta.msg}</Typography>
                </Box>

                {tiles.length > 0 && (
                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', [MQ_860]: { gridTemplateColumns: 'repeat(2,minmax(150px,1fr))' } }}>
                        {tiles.map((tile) => (
                            <HeroTile key={tile.label} icon={tile.icon} label={tile.label}>{tile.content}</HeroTile>
                        ))}
                    </Box>
                )}

                {meta.step >= 0 && stepLabels.length > 0 && (
                    <Stepper labels={stepLabels} current={meta.step} allDone={stepDone} />
                )}
            </Box>

            {(footLeft || onRefresh) && (
                <Box
                    sx={{
                        position: 'relative', zIndex: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px',
                        mt: '14px', pt: '12px', borderTop: '1px solid var(--h-tile-b)', fontSize: 11.5, color: 'var(--h-fg2)',
                    }}
                >
                    <span>{footLeft || ''}</span>
                    {onRefresh && (
                        <ButtonBase
                            className="no-print"
                            onClick={onRefresh}
                            disabled={refreshing}
                            aria-label={refreshLabel || 'Atualizar'}
                            sx={(t) => ({
                                display: 'inline-flex', alignItems: 'center', gap: '6px', bgcolor: 'var(--h-tile)',
                                border: '1px solid var(--h-tile-b)', color: 'var(--h-fg)', px: '11px', py: '6px', borderRadius: '20px',
                                fontSize: 11.5, fontWeight: 700, fontFamily: 'inherit', transition: 'background-color .15s, border-color .15s',
                                '&:hover': { bgcolor: 'rgba(255,255,255,.24)' },
                                '&.Mui-disabled': { color: 'var(--h-fg)', opacity: 0.85 },
                                '& svg': refreshing ? { animation: 'fv-spin .8s linear infinite' } : undefined,
                                '@keyframes fv-spin': { to: { transform: 'rotate(360deg)' } },
                                '&:focus-visible': { outline: `2px solid ${t.palette.primary.main}`, outlineOffset: 2 },
                            })}
                        >
                            <RefreshCw size={13} />
                            Atualizar
                        </ButtonBase>
                    )}
                </Box>
            )}
        </Box>
    );
}

function HeroTile({ icon, label, children }) {
    return (
        <Box sx={{ bgcolor: 'var(--h-tile)', border: '1px solid var(--h-tile-b)', borderRadius: '12px', p: '10px 12px' }}>
            <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: 10, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.05em', color: 'var(--h-fg2)', mb: '4px' }}>
                {icon}
                {label}
            </Box>
            <Box sx={{ '& b': { display: 'block', fontSize: 13.5, fontWeight: 700 }, '& small': { fontSize: 11, color: 'var(--h-fg2)' } }}>
                {children}
            </Box>
        </Box>
    );
}

function Stepper({ labels, current, allDone }) {
    const count = labels.length;
    return (
        <Box
            role="list"
            aria-label="Andamento"
            sx={{ display: 'grid', gridTemplateColumns: `repeat(${count},1fr)`, mt: '2px', [MQ_860]: { gridColumn: '1/-1' } }}
        >
            {labels.map((label, i) => {
                const done = i < current || (allDone && i === count - 1);
                const active = !done && i === current;
                const lit = done || active;
                return (
                    <Box
                        key={label}
                        role="listitem"
                        aria-current={active ? 'step' : undefined}
                        sx={{
                            position: 'relative', textAlign: 'center', fontSize: 10.5, fontWeight: 700, lineHeight: 1.25,
                            px: '2px', color: lit ? 'var(--h-fg)' : 'var(--h-fg2)',
                            ...(i > 0 && {
                                '&::before': {
                                    content: '""', position: 'absolute', top: 11, right: '50%', width: '100%', height: 3, zIndex: 0,
                                    bgcolor: lit ? 'var(--h-fg)' : 'var(--h-tile-b)',
                                },
                            }),
                        }}
                    >
                        <Box
                            sx={{
                                width: 26, height: 26, borderRadius: '50%', mx: 'auto', mb: '7px', display: 'flex',
                                alignItems: 'center', justifyContent: 'center', position: 'relative', zIndex: 1,
                                color: 'var(--h-ink)',
                                bgcolor: lit ? 'var(--h-fg)' : 'var(--h-tile)',
                                border: '2px solid', borderColor: lit ? 'var(--h-fg)' : 'var(--h-tile-b)',
                                ...(active && { boxShadow: '0 0 0 5px rgba(255,255,255,.28)' }),
                            }}
                        >
                            {done && <Check size={13} strokeWidth={3} />}
                            {active && (
                                <Box
                                    component="i"
                                    sx={{
                                        width: 8, height: 8, borderRadius: '50%', bgcolor: 'var(--h-ink)',
                                        animation: 'fv-pulse 1.6s ease-in-out infinite',
                                        '@keyframes fv-pulse': {
                                            '0%,100%': { transform: 'scale(1)', opacity: 1 },
                                            '50%': { transform: 'scale(.6)', opacity: 0.6 },
                                        },
                                        '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
                                    }}
                                />
                            )}
                        </Box>
                        {label}
                    </Box>
                );
            })}
        </Box>
    );
}

// ---------------------------------------------------------------------------
// Cards e blocos
// ---------------------------------------------------------------------------

export function Card({ children, sx, ...rest }) {
    return (
        <Box
            component="section"
            sx={[
                (t) => ({
                    bgcolor: 'background.paper', border: `1px solid ${t.palette.divider}`, borderRadius: '14px',
                    boxShadow: t.customShadows.sm, p: '16px', [MQ_520]: { p: '20px' },
                }),
                ...(Array.isArray(sx) ? sx : [sx]),
            ]}
            {...rest}
        >
            {children}
        </Box>
    );
}

export function CardHead({ icon, title, count, tone = 'info', sx }) {
    const purple = tone === 'purple';
    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', mb: '14px', ...sx }}>
            <Box
                sx={{
                    width: 30, height: 30, borderRadius: '9px', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
                    bgcolor: purple ? 'secondary.bg' : 'info.bg', color: purple ? 'secondary.main' : 'primary.main',
                }}
            >
                {icon}
            </Box>
            <Typography component="h2" sx={{ m: 0, fontSize: 14, fontWeight: 700, flex: 1 }}>{title}</Typography>
            {count && (
                <Box
                    component="span"
                    sx={(t) => ({
                        fontSize: 11, fontWeight: 700, color: 'text.disabled', bgcolor: 'background.subtle',
                        border: `1px solid ${t.palette.divider}`, px: '9px', py: '3px', borderRadius: '20px',
                    })}
                >
                    {count}
                </Box>
            )}
        </Box>
    );
}

export function KeyValue({ label, value, full, boxed }) {
    return (
        <Box
            sx={(t) => ({
                ...(full && { gridColumn: '1/-1' }),
                ...(boxed && {
                    bgcolor: 'background.subtle', border: `1px solid ${t.palette.divider}`, borderRadius: '11px', p: '10px 12px',
                }),
            })}
        >
            <Typography sx={{ fontSize: 10.5, fontWeight: 800, letterSpacing: '.05em', textTransform: 'uppercase', color: 'text.disabled', mb: '3px' }}>
                {label}
            </Typography>
            <Typography sx={{ fontSize: 13.5, fontWeight: 600, wordBreak: 'break-word' }}>{value ?? 'Não informado'}</Typography>
        </Box>
    );
}

export function SectionLabel({ children, first }) {
    return (
        <Box
            sx={{
                display: 'flex', alignItems: 'center', gap: '8px', fontSize: 10.5, fontWeight: 800, letterSpacing: '.06em',
                textTransform: 'uppercase', color: 'text.disabled', m: first ? '0 0 8px' : '16px 0 8px',
                '&::after': { content: '""', flex: 1, height: '1px', bgcolor: 'divider' },
            }}
        >
            {children}
        </Box>
    );
}

export function SummaryRow({ label, value, discount }) {
    return (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: '12px', fontSize: 13, py: '7px', color: discount ? 'error.main' : 'text.secondary' }}>
            <span>{label}</span>
            <Box component="b" sx={{ color: discount ? 'error.main' : 'text.primary', fontWeight: 700, whiteSpace: 'nowrap' }}>{value}</Box>
        </Box>
    );
}

/** Caixa verde do total (mesma da OS). */
export function TotalBox({ value, muted }) {
    return (
        <Box
            sx={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', mt: '8px', p: '14px', borderRadius: '14px',
                border: '1.5px solid', ...(muted
                    ? { bgcolor: 'background.subtle', borderColor: 'divider', color: 'text.disabled' }
                    : { bgcolor: 'success.bg', borderColor: 'success.main', color: 'success.main' }),
            }}
        >
            <Box component="span" sx={{ fontSize: 12, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.05em' }}>Total</Box>
            <Box component="b" sx={{ fontSize: 22, fontWeight: 800, letterSpacing: '-.02em' }}>{value}</Box>
        </Box>
    );
}

export function ContactButton({ kind, href, large }) {
    const whatsapp = kind === 'whatsapp';
    return (
        <Button
            component="a"
            href={href}
            {...(whatsapp ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
            variant={whatsapp ? 'contained' : 'outlined'}
            color={whatsapp ? 'success' : 'primary'}
            startIcon={whatsapp ? <MessageCircle size={large ? 17 : 16} /> : <Phone size={large ? 17 : 16} />}
            sx={{
                flex: 1, whiteSpace: 'nowrap', fontSize: 13, fontWeight: 700, borderRadius: '11px',
                p: large ? '12px 14px' : '11px 16px',
                ...(whatsapp && { color: '#fff', bgcolor: 'success.main', '&:hover': { bgcolor: 'success.main', filter: 'brightness(1.06)' } }),
            }}
        >
            {whatsapp ? 'WhatsApp' : 'Ligar'}
        </Button>
    );
}

export function InfoLine({ icon, children }) {
    return (
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: '10px', fontSize: 12.5, color: 'text.secondary', py: '7px', lineHeight: 1.45 }}>
            <Box sx={{ color: 'primary.main', display: 'flex', flexShrink: 0, mt: '1px' }}>{icon}</Box>
            <span>{children}</span>
        </Box>
    );
}

/** Barra fixa inferior (mobile) das telas públicas. */
export function StickyBar({ children }) {
    return (
        <Box
            className="no-print"
            sx={(t) => ({
                position: 'sticky', bottom: 0, zIndex: 20, mt: 'auto',
                bgcolor: 'background.paper', borderTop: `1px solid ${t.palette.divider}`,
                p: '10px 16px calc(10px + env(safe-area-inset-bottom))',
                display: 'flex', gap: '10px', boxShadow: '0 -6px 20px rgba(15,23,42,.08)',
                [MQ_860]: { display: 'none' },
            })}
        >
            {children}
        </Box>
    );
}

// ---------------------------------------------------------------------------
// Carregando (skeleton) e feedback
// ---------------------------------------------------------------------------

export function Skel({ h, w = '100%', mb = 0, round, sx }) {
    return (
        <Box
            sx={{
                height: h, width: w, mb: `${mb}px`, borderRadius: round ? '14px' : '8px',
                backgroundImage: (t) => `linear-gradient(90deg, ${t.palette.background.subtle} 0%, ${t.palette.divider} 50%, ${t.palette.background.subtle} 100%)`,
                backgroundSize: '200% 100%', animation: 'fv-shimmer 1.3s ease-in-out infinite',
                '@keyframes fv-shimmer': { from: { backgroundPosition: '200% 0' }, to: { backgroundPosition: '-200% 0' } },
                '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
                ...sx,
            }}
        />
    );
}

export function LoadingView({ text }) {
    return (
        <>
            <Box component="header" sx={(t) => ({ bgcolor: 'background.paper', borderBottom: `1px solid ${t.palette.divider}` })}>
                <Box sx={{ maxWidth: 1040, mx: 'auto', display: 'flex', alignItems: 'center', gap: '12px', p: '14px 16px', [MQ_520]: { p: '16px 24px' } }}>
                    <Skel h={48} w={48} round sx={{ flexShrink: 0 }} />
                    <Box sx={{ flex: 1 }}>
                        <Skel h={14} w="60%" mb={8} />
                        <Skel h={10} w="35%" />
                    </Box>
                </Box>
            </Box>
            <PortalMain aria-busy="true" aria-live="polite">
                <Card sx={{ p: '20px' }}>
                    <Skel h={11} w="34%" mb={16} />
                    <Skel h={24} w="62%" mb={12} />
                    <Skel h={12} w="90%" mb={6} />
                    <Skel h={12} w="70%" mb={22} />
                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                        <Skel h={56} />
                        <Skel h={56} />
                    </Box>
                </Card>
                <Card>
                    <Skel h={14} w="30%" mb={16} />
                    <Skel h={54} mb={8} />
                    <Skel h={54} />
                </Card>
                <Card>
                    <Skel h={14} w="40%" mb={16} />
                    <Skel h={44} mb={8} />
                    <Skel h={44} mb={8} />
                    <Skel h={44} />
                </Card>
                <Typography sx={{ textAlign: 'center', fontSize: 12.5, color: 'text.disabled', fontWeight: 600 }}>
                    {text}
                </Typography>
            </PortalMain>
        </>
    );
}

/**
 * @param config { tone: warning|danger|neutral, Icon, title, text, help?: node[], retry?: bool }
 */
export function FeedbackView({ config, onRetry, footText }) {
    const { Icon } = config;
    const toneSx = {
        warning: { borderColor: 'warning.main', bgcolor: 'warning.bg', color: 'warning.main', borderStyle: 'solid' },
        danger: { borderColor: 'error.main', bgcolor: 'error.bg', color: 'error.main', borderStyle: 'solid' },
        neutral: { borderColor: 'divider', color: 'text.disabled', borderStyle: 'dashed' },
    }[config.tone];

    return (
        <>
            <Box
                component="main"
                role="alert"
                sx={{ maxWidth: 460, width: '100%', mx: 'auto', p: '56px 20px 30px', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center' }}
            >
                <Box sx={{ width: 78, height: 78, borderRadius: '50%', border: '2px solid', display: 'flex', alignItems: 'center', justifyContent: 'center', mb: '20px', ...toneSx }}>
                    <Icon size={32} strokeWidth={1.8} />
                </Box>
                <Typography component="h1" sx={{ m: 0, mb: '8px', fontSize: 20, fontWeight: 800, letterSpacing: '-.02em' }}>{config.title}</Typography>
                <Typography sx={{ fontSize: 13.5, color: 'text.secondary', lineHeight: 1.6 }}>{config.text}</Typography>
                {config.help && (
                    <Box
                        sx={(t) => ({
                            mt: '20px', textAlign: 'left', width: '100%', bgcolor: 'background.paper', border: `1px solid ${t.palette.divider}`,
                            borderRadius: '12px', p: '14px 16px', fontSize: 12.5, color: 'text.secondary',
                        })}
                    >
                        <Box sx={{ fontSize: 11, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.05em', color: 'text.disabled', mb: '8px' }}>
                            O que fazer
                        </Box>
                        <Box component="ul" sx={{ m: 0, pl: '18px', lineHeight: 1.7, '& b': { color: 'text.primary' } }}>
                            {config.help.map((item, idx) => <li key={idx}>{item}</li>)}
                        </Box>
                    </Box>
                )}
                {config.retry && (
                    <Button variant="contained" onClick={onRetry} startIcon={<RefreshCw size={15} />} sx={{ mt: '20px', p: '11px 16px', fontSize: 13, fontWeight: 700, borderRadius: '11px' }}>
                        Tentar novamente
                    </Button>
                )}
            </Box>
            <PoweredBy text={footText} />
        </>
    );
}
